import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { downloadDocument } from '../../services/downloadService';
import { getToken } from '../../services/tokenService';
import { isImageFile, isVideoFile, ACCEPTED_EXT, MAX_FILE_SIZE_MB, PAGE_SIZE } from './videoManagerHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useVideoManager({ userRole }) {
  const { getAuthHeader } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ index: 0, total: 0, percent: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | image | video
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Sélection multiple
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Pagination + infinite scroll
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const skipRef = useRef(0);

  // ZIP dialog
  const [zipDialogOpen, setZipDialogOpen] = useState(false);
  const [zipName, setZipName] = useState('');
  const [zipMessage, setZipMessage] = useState('');
  const [zipShare, setZipShare] = useState(userRole === 'member');
  const [zipSubmitting, setZipSubmitting] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMedia();
  }, []);  // eslint-disable-line

  // Charger la première page (reset)
  const fetchMedia = useCallback(async () => {
    try {
      setLoading(true);
      skipRef.current = 0;
      const headers = getAuthHeader();
      const response = await axios.get(`${BACKEND_URL}/api/ged/documents/paginated`, {
        headers,
        params: { skip: 0, limit: PAGE_SIZE }
      });
      const data = response.data || {};
      const all = data.items || [];
      // Filtrer côté client photos+vidéos uniquement
      const visual = all.filter((doc) => isImageFile(doc) || isVideoFile(doc));
      setMedia(visual);
      setTotalCount(data.total || 0);
      setHasMore(Boolean(data.has_more));
      skipRef.current = all.length;
    } catch {
      setMedia([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);  // eslint-disable-line

  // Charger la page suivante (append)
  const fetchMoreMedia = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    try {
      setLoadingMore(true);
      const headers = getAuthHeader();
      const response = await axios.get(`${BACKEND_URL}/api/ged/documents/paginated`, {
        headers,
        params: { skip: skipRef.current, limit: PAGE_SIZE }
      });
      const data = response.data || {};
      const all = data.items || [];
      const visual = all.filter((doc) => isImageFile(doc) || isVideoFile(doc));
      setMedia((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const newOnes = visual.filter((m) => !seen.has(m.id));
        return [...prev, ...newOnes];
      });
      setTotalCount(data.total || 0);
      setHasMore(Boolean(data.has_more));
      skipRef.current = skipRef.current + all.length;
    } catch {
      // silencieux : pas de toast pour ne pas spammer en scroll
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore]);  // eslint-disable-line

  // Intersection Observer pour déclencher fetchMoreMedia
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          fetchMoreMedia();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, fetchMoreMedia]);

  const validateFile = (file) => {
    const name = file.name.toLowerCase();
    const ext = name.split('.').pop();
    if (!ACCEPTED_EXT.includes(ext)) {
      return `Format non supporté (${ext}). Formats acceptés : ${ACCEPTED_EXT.join(', ')}`;
    }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      return `Fichier trop volumineux (${sizeMB.toFixed(1)} Mo). Limite : ${MAX_FILE_SIZE_MB} Mo.`;
    }
    return null;
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const valid = [];
    for (const f of files) {
      const err = validateFile(f);
      if (err) {
        toast.error(`${f.name}: ${err}`);
      } else {
        valid.push(f);
      }
    }
    if (!valid.length) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    await uploadFiles(valid);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    setUploadProgress({ index: 0, total: files.length, percent: 0 });
    let okCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ index: i + 1, total: files.length, percent: 0 });
      const formData = new FormData();
      formData.append('file', file);
      const cat = file.type.startsWith('image/') ? 'photo' : 'video';
      formData.append('categorie', cat);
      formData.append('description', `${cat === 'photo' ? 'Photo' : 'Vidéo'} uploadée: ${file.name}`);

      try {
        await axios.post(`${BACKEND_URL}/api/ged/upload`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);
            setUploadProgress({ index: i + 1, total: files.length, percent });
          }
        });
        okCount += 1;
      } catch (error) {
        toast.error(`Échec upload de ${file.name}: ${error.response?.data?.detail || 'erreur serveur'}`);
      }
    }

    setUploading(false);
    setUploadProgress({ index: 0, total: 0, percent: 0 });
    if (okCount > 0) {
      toast.success(`${okCount} fichier${okCount > 1 ? 's' : ''} importé${okCount > 1 ? 's' : ''} avec succès`);
      fetchMedia();
    }
  };

  const handleDownload = async (item) => {
    try {
      await downloadDocument(item);
      toast.success('Téléchargement démarré');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/ged/documents/${itemToDelete.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Fichier supprimé');
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(itemToDelete.id);
        return next;
      });
      fetchMedia();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectAllVisible = (visibleItems) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleItems.forEach((m) => next.add(m.id));
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ged/documents/bulk-delete`,
        { document_ids: Array.from(selectedIds) },
        { headers: getAuthHeader() }
      );
      toast.success(response.data?.message || `${selectedIds.size} fichier(s) supprimé(s)`);
      setBulkDeleteOpen(false);
      clearSelection();
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openZipDialog = () => {
    if (selectedIds.size === 0) {
      toast.error('Sélectionnez au moins un fichier');
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    setZipName(`Dossier_${today}`);
    setZipMessage('');
    setZipShare(userRole === 'member');
    setZipDialogOpen(true);
  };

  const handleCreateZip = async () => {
    if (selectedIds.size === 0) return;
    setZipSubmitting(true);
    try {
      const payload = {
        document_ids: Array.from(selectedIds),
        zip_name: (zipName || 'dossier_partage').trim(),
        share_with_cooper: zipShare,
        message: zipMessage || undefined,
      };

      if (zipShare) {
        // Mode partage → JSON response, ZIP stocké en GED + notif Cooper
        const response = await axios.post(
          `${BACKEND_URL}/api/ged/documents/zip-package`,
          payload,
          { headers: getAuthHeader() }
        );
        const data = response.data || {};
        if (data.cooper_notified) {
          toast.success(`Dossier "${data.zip_filename}" envoyé à votre COOPER'S (${data.included} fichier(s))`);
        } else if (data.shared_with_cooper === false && data.warning) {
          toast.warning(data.warning);
        } else {
          toast.success(`Dossier "${data.zip_filename}" créé. Aucun COOPER référent trouvé — récupérez-le via le lien ci-dessous.`);
        }
        // Téléchargement local du ZIP également
        if (data.download_url) {
          const link = document.createElement('a');
          link.href = `${BACKEND_URL}${data.download_url}?token=${encodeURIComponent(getToken())}`;
          link.download = data.zip_filename || 'partage.zip';
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
        fetchMedia();
      } else {
        // Mode téléchargement direct
        const response = await axios.post(
          `${BACKEND_URL}/api/ged/documents/zip-package`,
          payload,
          { headers: getAuthHeader(), responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${payload.zip_name}.zip`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`ZIP "${payload.zip_name}.zip" téléchargé`);
      }
      setZipDialogOpen(false);
      clearSelection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du ZIP');
    } finally {
      setZipSubmitting(false);
    }
  };

  const openPreview = (item) => {
    setSelectedItem(item);
    setPreviewOpen(true);
  };

  const filteredMedia = useMemo(() => {
    return media.filter((m) => {
      if (typeFilter === 'image' && !isImageFile(m)) return false;
      if (typeFilter === 'video' && !isVideoFile(m)) return false;
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      const name = (m.original_filename || m.nom || m.filename || '').toLowerCase();
      const desc = (m.description || '').toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [media, searchTerm, typeFilter]);

  const stats = useMemo(() => {
    const photos = media.filter(isImageFile).length;
    const videos = media.filter(isVideoFile).length;
    const totalSize = media.reduce((acc, m) => acc + (m.file_size || m.taille || 0), 0);
    return { photos, videos, totalSize };
  }, [media]);

  return {
    media, loading, uploading, uploadProgress,
    searchTerm, setSearchTerm, typeFilter, setTypeFilter,
    selectedItem, previewOpen, setPreviewOpen,
    deleteConfirmOpen, setDeleteConfirmOpen, itemToDelete, setItemToDelete,
    selectedIds, setSelectedIds, bulkDeleteOpen, setBulkDeleteOpen, bulkDeleting,
    totalCount, hasMore, loadingMore, sentinelRef,
    zipDialogOpen, setZipDialogOpen, zipName, setZipName, zipMessage, setZipMessage,
    zipShare, setZipShare, zipSubmitting,
    fileInputRef,
    handleFileSelect, handleDownload, handleDelete, toggleSelect, clearSelection,
    selectAllVisible, handleBulkDelete, openZipDialog, handleCreateZip, openPreview,
    fetchMoreMedia,
    filteredMedia, stats,
  };
}
